import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, ArrowRight } from "lucide-react";
import Logo from "./Logo";
import { navLinks, site } from "../data/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  const solid = scrolled;

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          solid ? "bg-sand/90 backdrop-blur-md border-b border-forest/10 shadow-sm shadow-forest/5" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="flex items-center justify-between h-[72px]">
            <Logo light={!solid} />
            <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
              {navLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-link-${l.to.replace("/", "") || "accueil"}`}
                  className={({ isActive }) =>
                    `text-[12px] xl:text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                      solid
                        ? isActive
                          ? "text-moss font-semibold"
                          : "text-coal hover:text-moss"
                        : isActive
                          ? "text-sand font-semibold"
                          : "text-sand/80 hover:text-sand"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/demander-un-devis"
                data-testid="nav-devis-button"
                className={`btn text-[13px] px-5 xl:px-6 py-3 ${
                  solid ? "bg-forest text-sand hover:bg-forest-dark" : "bg-sand text-forest hover:bg-white"
                } hover:-translate-y-0.5 hover:shadow-lg hover:shadow-forest/20`}
              >
                Demander un devis
                <ArrowRight size={15} />
              </Link>
            </nav>
            <div className="flex items-center gap-3 lg:hidden">
              <a
                href={site.phoneHref}
                data-testid="nav-phone-link"
                className={`p-2.5 rounded-full transition-colors ${solid ? "bg-forest/10 text-forest" : "bg-white/15 text-sand backdrop-blur"}`}
                aria-label="Appeler Embek Group"
              >
                <Phone size={17} />
              </a>
              <button
                onClick={() => setOpen(!open)}
                data-testid="nav-burger-button"
                aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
                className={`p-2.5 rounded-full transition-colors ${solid ? "bg-forest text-sand" : "bg-white/15 text-sand backdrop-blur"}`}
              >
                {open ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-forest-dark grain lg:hidden"
          >
            <div className="h-full overflow-y-auto px-6 pt-28 pb-10 flex flex-col">
              <nav className="flex flex-col gap-1">
                {navLinks.map((l, i) => (
                  <motion.div
                    key={l.to}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <NavLink
                      to={l.to}
                      className={({ isActive }) =>
                        `flex items-center justify-between py-3.5 border-b border-sand/10 font-serif text-3xl tracking-tight ${
                          isActive ? "text-moss" : "text-sand"
                        }`
                      }
                    >
                      {l.label}
                      <ArrowRight size={20} className="text-sand/40" />
                    </NavLink>
                  </motion.div>
                ))}
              </nav>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.5 }}
                className="mt-auto pt-10"
              >
                <Link to="/demander-un-devis" data-testid="mobile-menu-devis-button" className="btn-light w-full">
                  Demander un devis
                  <ArrowRight size={15} />
                </Link>
                <p className="mt-6 text-xs text-sand/50 tracking-wide">{site.phone} — {site.email}</p>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
